import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '../../../elements/button'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas-pro'
import { trackEvent } from '../../../_common/functions'
import Text from '../../../elements/Typo/Text'
import Icon from '../../../components/icons'
import { useFpLoader } from '../../../store/api/hooks/fpLoaderHook'

interface Props {
    experiment: any
    contentRef: any
    working: boolean
    failed: boolean
    isGraphExecuting: boolean
}

const DownloadButton: React.FC<Props> = ({ experiment, contentRef, working, failed, isGraphExecuting }) => {
    const { t } = useTranslation()
    const { showLoader, hideLoader } = useFpLoader()
    const [isGeneratingPdf, setIsGeneratingPdf] = useState<boolean>(false)

    const _handlePdfGenerator = async () => {
        if (!contentRef.current) return
        showLoader()
        try {
            // console.log('Generating PDF...')
            setIsGeneratingPdf(true)
            showLoader()

            // A4 size in mm
            const pageWidth = 210
            const pageHeight = 297
            const margin = 15

            // Content width (inside margins)
            const contentWidth = pageWidth - margin * 2
            // const contentHeight = pageHeight - margin * 2

            const pdf = new jsPDF('p', 'mm', 'a4')

            // First, capture the header/summary section
            const headerSection = contentRef.current.querySelector('#pdf-header')
            const groupsSection = contentRef.current.querySelector('#pdf-groups')

            // Initialize currentY position
            let currentY = margin

            // Process header
            if (headerSection) {
                const headerCanvas = await html2canvas(headerSection, {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    allowTaint: true,
                })

                const headerImgWidth = contentWidth
                const headerImgHeight = (headerCanvas.height * headerImgWidth) / headerCanvas.width

                pdf.addImage(headerCanvas.toDataURL('image/png'), 'PNG', margin, currentY, headerImgWidth, headerImgHeight)

                currentY += headerImgHeight + 5
            }

            // Process groups section
            if (groupsSection) {
                const groupsCanvas = await html2canvas(groupsSection, {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    allowTaint: true,
                })

                const groupsImgWidth = contentWidth
                const groupsImgHeight = (groupsCanvas.height * groupsImgWidth) / groupsCanvas.width

                // Check if we need a new page
                if (currentY + groupsImgHeight > pageHeight - margin) {
                    pdf.addPage()
                    currentY = margin
                }

                pdf.addImage(groupsCanvas.toDataURL('image/png'), 'PNG', margin, currentY, groupsImgWidth, groupsImgHeight)

                currentY += groupsImgHeight + 10
            }

            // Process each chart individually
            const graphContainers: HTMLElement[] = Array.from(contentRef.current.querySelectorAll('.graph-container'))
            for (const graphContainer of graphContainers) {
                const graphCanvas = await html2canvas(graphContainer, {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    allowTaint: true,
                })
                const graphImgWidth = contentWidth
                const graphImgHeight = (graphCanvas.height * graphImgWidth) / graphCanvas.width

                // Check if the graph fits on current page, if not add a new page
                if (currentY + graphImgHeight > pageHeight - margin) {
                    pdf.addPage()
                    currentY = margin
                }

                pdf.addImage(graphCanvas.toDataURL('image/png'), 'PNG', margin, currentY, graphImgWidth, graphImgHeight)

                currentY += graphImgHeight + 10
            }

            // pdf.save('group-allocation.pdf')
            const pdfOutput = pdf.output('blob')
            const url = URL.createObjectURL(pdfOutput)
            window.open(url, '_blank')
            URL.revokeObjectURL(url)
            setIsGeneratingPdf(false)
            hideLoader()
            // console.log('PDF generated successfully!')
        } catch (error) {
            console.error(t('Error generating PDF:'), error)
            setIsGeneratingPdf(false)
            hideLoader()
        }
    }

    return (
        <Button
            size="sm"
            className="bg-gradient-to-b from-[#E5B611] to-[#F5E003]"
            disabled={working || isGeneratingPdf || failed || isGraphExecuting}
            onClick={() => {
                void _handlePdfGenerator()
                trackEvent('pdf_download_clicked', {
                    experiment_id: experiment?.id,
                })
            }}
        >
            <Text>{t('Download Result as a PDF')}</Text>
            <Icon name="download-alt"></Icon>
        </Button>
    )
}

export default DownloadButton
